import React, { useState, useEffect } from 'react';
import {
  CreateVendedorData,
  createVendedor,
  createUser,
  checkUsuarioExists,
  getSelectEmpresas,
  getSelectSupervisores,
  PERMISSION_FIELDS,
} from '../../../features/user/services/usersAPI';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import styles from './UserFormModal.module.css';

export interface UserFormInitialData {
  nome?: string;
  usuario?: string;
  tipo?: 'vendedor' | 'representante' | 'comum';
  permissions?: Record<string, number>; // 0/1 values from DB
}

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: UserFormInitialData;
}

interface FormData {
  nome: string;
  usuario: string;
  senha: string;
  tipo: 'vendedor' | 'representante' | 'comum';
  empresa: string;
  regional: string;
  supervisor: string;
  permissions: Record<string, boolean>;
}

/** Constrói o estado inicial de permissões (todas false, exceto Dashboard) */
const buildInitialPermissions = (): Record<string, boolean> => {
  const perms: Record<string, boolean> = {};
  for (const p of PERMISSION_FIELDS) {
    perms[p.key] = p.key === 'UsuarioDeshboard';
  }
  return perms;
};

const initialFormData: FormData = {
  nome: '',
  usuario: '',
  senha: '',
  tipo: 'vendedor',
  empresa: '',
  regional: '',
  supervisor: '',
  permissions: buildInitialPermissions(),
};

const estados = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO',
  'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI',
  'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const UserFormModal: React.FC<UserFormModalProps> = ({ isOpen, onClose, onSuccess, initialData }) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [empresas, setEmpresas] = useState<string[]>([]);
  const [supervisores, setSupervisores] = useState<string[]>([]);

  const [usuarioExists, setUsuarioExists] = useState(false);
  const [checkingUsuario, setCheckingUsuario] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setError('');

    if (initialData) {
      const perms = buildInitialPermissions();
      if (initialData.permissions) {
        for (const [key, val] of Object.entries(initialData.permissions)) {
          perms[key] = val === 1;
        }
      }
      setFormData({
        ...initialFormData,
        nome: initialData.nome || '',
        usuario: initialData.usuario || '',
        tipo: initialData.tipo || 'vendedor',
        permissions: perms,
      });
    } else {
      setFormData(initialFormData);
    }
    setUsuarioExists(false);

    const loadSelects = async () => {
      setLoading(true);
      try {
        const [emp, sup] = await Promise.all([
          getSelectEmpresas(),
          getSelectSupervisores(),
        ]);
        setEmpresas(emp);
        setSupervisores(sup);
      } catch (err) {
        console.error('Erro ao carregar selects:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSelects();
  }, [isOpen]);

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');

    if (field === 'tipo') {
      setFormData(prev => ({
        ...prev,
        tipo: value as 'vendedor' | 'representante' | 'comum',
        empresa: '',
        regional: '',
        supervisor: '',
      }));
    }

    if (field === 'usuario' && typeof value === 'string' && value.trim().length >= 3) {
      checkDuplicidade(value.trim());
    }
  };

  const handlePermissionChange = (key: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: checked },
    }));
    setError('');
  };

  const checkDuplicidade = async (usuario: string) => {
    setCheckingUsuario(true);
    try {
      const result = await checkUsuarioExists(usuario);
      setUsuarioExists(result.exists);
    } catch {
      setUsuarioExists(false);
    } finally {
      setCheckingUsuario(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.nome.trim()) { setError('Nome é obrigatório'); return; }
    if (!formData.usuario.trim()) { setError('Usuário é obrigatório'); return; }
    if (!formData.senha.trim()) { setError('Senha é obrigatória'); return; }
    if (formData.tipo === 'vendedor' && !formData.empresa) { setError('Empresa é obrigatória para vendedores'); return; }
    if (usuarioExists && !initialData) { setError('Este nome de usuário já existe'); return; }

    setSaving(true);
    try {
      if (formData.tipo === 'comum') {
        // Converter Record<string, boolean> → objeto com 0/1
        const permValues: Record<string, number> = {};
        for (const [key, val] of Object.entries(formData.permissions)) {
          permValues[key] = val ? 1 : 0;
        }
        await createUser({
          USUARIO: formData.usuario.trim(),
          NOME: formData.nome.trim(),
          SENHA: formData.senha.trim(),
          ...permValues,
        } as any);
      } else {
        const payload: CreateVendedorData = {
          nome: formData.nome.trim(),
          usuario: formData.usuario.trim(),
          senha: formData.senha.trim(),
          tipo: formData.tipo,
        };
        if (formData.tipo === 'vendedor') {
          payload.empresa = formData.empresa;
          payload.regional = formData.regional || undefined;
          payload.supervisor = formData.supervisor || undefined;
        }
        await createVendedor(payload);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao criar usuário');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Novo Usuário</h2>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {loading ? (
            <div className={styles.loadingWrapper}><LoadingSpinner /></div>
          ) : (
            <>
              {error && <div className={styles.error}>{error}</div>}

              {/* Seletor de tipo */}
              <div className={styles.tipoSelectorContainer}>
                <span className={styles.tipoLabel}>Perfil do Usuário</span>
                <div className={styles.tipoOptions}>
                  <div
                    className={`${styles.tipoOptionCard} ${formData.tipo === 'vendedor' ? styles.tipoOptionCardActive : ''}`}
                    onClick={() => handleChange('tipo', 'vendedor')}
                  >
                    <div className={styles.tipoOptionText}>
                      <span className={styles.tipoOptionLabel}>Vendedor</span>
                      <span className={styles.tipoOptionDescription}>
                        Possui base de faturamento e carteira de clientes associada.
                      </span>
                    </div>
                    {formData.tipo === 'vendedor' ? (
                      <CheckCircleIcon className={styles.checkIconActive} />
                    ) : (
                      <CheckCircleOutlineIcon className={styles.checkIconInactive} />
                    )}
                  </div>

                  <div
                    className={`${styles.tipoOptionCard} ${formData.tipo === 'representante' ? styles.tipoOptionCardActive : ''}`}
                    onClick={() => handleChange('tipo', 'representante')}
                  >
                    <div className={styles.tipoOptionText}>
                      <span className={styles.tipoOptionLabel}>Representante</span>
                      <span className={styles.tipoOptionDescription}>
                        Parceiro comercial externo sem base de faturamento direto.
                      </span>
                    </div>
                    {formData.tipo === 'representante' ? (
                      <CheckCircleIcon className={styles.checkIconActive} />
                    ) : (
                      <CheckCircleOutlineIcon className={styles.checkIconInactive} />
                    )}
                  </div>

                  <div
                    className={`${styles.tipoOptionCard} ${formData.tipo === 'comum' ? styles.tipoOptionCardActive : ''}`}
                    onClick={() => handleChange('tipo', 'comum')}
                  >
                    <div className={styles.tipoOptionText}>
                      <span className={styles.tipoOptionLabel}>Usuário Comum</span>
                      <span className={styles.tipoOptionDescription}>
                        Acesso ao sistema sem vínculo comercial. Permissões configuráveis.
                      </span>
                    </div>
                    {formData.tipo === 'comum' ? (
                      <CheckCircleIcon className={styles.checkIconActive} />
                    ) : (
                      <CheckCircleOutlineIcon className={styles.checkIconInactive} />
                    )}
                  </div>
                </div>
              </div>

              {/* Campos base: Nome, Usuário, Senha — sempre em grid */}
              <div className={formData.tipo === 'comum' ? styles.gridThreeCols : styles.grid}>
                <div className={styles.field}>
                  <label className={styles.label}>Nome completo *</label>
                  <input type="text" className={styles.input} value={formData.nome}
                    onChange={e => handleChange('nome', e.target.value)}
                    placeholder="Ex: NOME COMPLETO" autoFocus />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Usuário (login) *</label>
                  <div className={styles.inputWrapper}>
                    <input type="text" className={`${styles.input} ${usuarioExists ? styles.inputError : ''}`}
                      value={formData.usuario} onChange={e => handleChange('usuario', e.target.value)}
                      placeholder="Nome de login" />
                    {checkingUsuario && <span className={styles.inputStatus}>Verificando...</span>}
                    {usuarioExists && <span className={styles.inputStatusError}>Já existe</span>}
                  </div>
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Senha *</label>
                  <input type="text" className={styles.input} value={formData.senha}
                    onChange={e => handleChange('senha', e.target.value)}
                    placeholder="Senha de acesso" />
                </div>

                {formData.tipo === 'vendedor' && (
                  <>
                    <div className={styles.field}>
                      <label className={styles.label}>Empresa *</label>
                      <select className={styles.select} value={formData.empresa}
                        onChange={e => handleChange('empresa', e.target.value)}>
                        <option value="">Selecione...</option>
                        {empresas.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </div>

                    <div className={styles.field}>
                      <label className={styles.label}>Regional</label>
                      <select className={styles.select} value={formData.regional}
                        onChange={e => handleChange('regional', e.target.value)}>
                        <option value="">Selecione...</option>
                        {estados.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                      </select>
                    </div>

                    <div className={styles.field}>
                      <label className={styles.label}>Supervisor</label>
                      <select className={styles.select} value={formData.supervisor}
                        onChange={e => handleChange('supervisor', e.target.value)}>
                        <option value="">Selecione...</option>
                        {supervisores.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </div>
                  </>
                )}
              </div>

              {/* Permissões abaixo dos campos — somente para Usuário Comum */}
              {formData.tipo === 'comum' && (
                <div className={styles.permissionsBlock}>
                  <span className={styles.permissionsTitle}>Permissões de Acesso</span>
                  <div className={styles.permissionsGrid}>
                    {PERMISSION_FIELDS.map(perm => (
                      <label
                        key={perm.key}
                        className={`${styles.permissionItem} ${formData.permissions[perm.key] ? styles.permissionItemChecked : ''}`}
                      >
                        <input
                          type="checkbox"
                          className={styles.permissionCheckbox}
                          checked={formData.permissions[perm.key] || false}
                          onChange={e => handlePermissionChange(perm.key, e.target.checked)}
                        />
                        <span className={styles.permissionLabel}>{perm.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.actions}>
                <button type="button" className={styles.cancelBtn} onClick={onClose}>Cancelar</button>
                <button type="submit" className={styles.saveBtn} disabled={saving || (usuarioExists && !initialData)}>
                  {saving ? 'Criando...' : `Criar ${formData.tipo === 'vendedor' ? 'Vendedor' : formData.tipo === 'representante' ? 'Representante' : 'Usuário'}`}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;